import React, { ReactNode, useCallback, useState } from 'react';
import { ModalsKeys, Nullable } from 'global';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, FormProvider } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router-dom';
import { ModalsContainer } from 'components/shared/Modals';
import { VendorOpportunity } from 'modules/vendorOpportunity';
import {
  CreateChargesDialog,
  EquipmentChargesDialog,
  CreateUpdateEquipmentDialog,
  UploadImagesForNewEquipmentDialog,
} from 'modules/equipment';

import { dealBuilderFormSchema } from '../model/schema';
import { DealBuilderWrapper } from './DealBuilderWrapper';
import { DealBuilderCreditApplicationSource, DealBuilderEquipmentSource, DealBuilderFormValues } from '../types';
import {
  DEAL_BUILDER_DEFAULT_VALUES,
  DEAL_BUILDER_QUERY_PARAM_STEP,
  DEAL_BUILDER_QUERY_PARAM_PROGRESS,
} from '../constants';

const modalsWithKeys: Partial<Record<ModalsKeys, ReactNode>> = {
  [ModalsKeys.ChargesEquipmentDialog]: <EquipmentChargesDialog />,
  [ModalsKeys.CreateChargesEquipmentDialog]: <CreateChargesDialog />,
  [ModalsKeys.CreateUpdateEquipmentDialog]: <CreateUpdateEquipmentDialog />,
  [ModalsKeys.UploadImagesForNewEquipmentDialog]: <UploadImagesForNewEquipmentDialog />,
};

interface Props {
  vo: VendorOpportunity;
  creditApplication: DealBuilderCreditApplicationSource;
  equipmentData: Nullable<DealBuilderEquipmentSource[]>;
}

export const DealBuilderTab = ({ vo, creditApplication, equipmentData }: Props) => {
  const [remountKey, setRemountKey] = useState(0);
  const [activeProposalId, setActiveProposalId] = useState<Nullable<string>>(null);

  const { replace } = useHistory();
  const location = useLocation();
  // An explicit step in the URL on entry (refresh, external navigation) must be kept as is
  const [shouldAutoLand, setShouldAutoLand] = useState(
    () => !new URLSearchParams(location.search).has(DEAL_BUILDER_QUERY_PARAM_STEP)
  );

  const formMethods = useForm<DealBuilderFormValues>({
    resolver: zodResolver(dealBuilderFormSchema),
    defaultValues: DEAL_BUILDER_DEFAULT_VALUES,
  });

  const remount = useCallback(
    (proposalId?: string, shouldLand = true) => {
      if (proposalId !== undefined) setActiveProposalId(proposalId);
      formMethods.reset(DEAL_BUILDER_DEFAULT_VALUES);
      const params = new URLSearchParams(location.search);
      params.delete(DEAL_BUILDER_QUERY_PARAM_STEP);
      params.delete(DEAL_BUILDER_QUERY_PARAM_PROGRESS);
      replace({ pathname: location.pathname, hash: location.hash, search: params.toString() });
      setShouldAutoLand(shouldLand);
      setRemountKey(k => k + 1);
    },
    [formMethods, location, replace]
  );

  return (
    <>
      <FormProvider {...formMethods} key={remountKey}>
        <DealBuilderWrapper
          vo={vo}
          onRemount={remount}
          equipmentData={equipmentData}
          shouldAutoLand={shouldAutoLand}
          activeProposalId={activeProposalId}
          creditApplication={creditApplication}
        />
      </FormProvider>

      <ModalsContainer modalsWithKeys={modalsWithKeys} />
    </>
  );
};
